import { getReviews } from './reviewService';
import { getYearData } from './yearService';
import { logger } from '../utils/logger';
import {
  getTrafficValue,
  calculateSeoScore,
  estimateSeoScore,
  sortBusinessesBySeoScore,
} from '../utils/competitiveIntelHelpers';

const DEFAULT_HISTORICAL_DATA = {
  monthlyReviewCounts: {},
  monthlyRatings: {},
  sentimentBreakdown: { positive: 0, neutral: 0, negative: 0 }
};

const getBusinessKey = (business) =>
  business?.maps_uri || business?.mapsUri || business?.name || business?.business_name;

const normalizeHistoricalData = (yearRecord) => {
  const data = yearRecord?.data || {};
  return {
    ...data,
    monthlyReviewCounts:
      data.monthlyReviewCounts || data.monthly_review_counts || {},
    monthlyRatings: data.monthlyRatings || data.monthly_ratings || {},
    sentimentBreakdown:
      data.sentimentBreakdown ||
      data.sentiment_breakdown ||
      DEFAULT_HISTORICAL_DATA.sentimentBreakdown
  };
};

const calculateThreeMonthGrowth = (historicalData) => {
  const monthlyReviewCounts = historicalData?.monthlyReviewCounts || {};
  const months = Object.keys(monthlyReviewCounts).sort();
  if (months.length === 0) return 0;
  const lastThreeMonths = months.length >= 3 ? months.slice(-3) : months;
  return lastThreeMonths.reduce(
    (sum, month) => sum + (monthlyReviewCounts[month] || 0),
    0
  );
};

const calculateSentimentScore = (historicalData) => {
  const sentimentBreakdown = historicalData?.sentimentBreakdown || {
    positive: 0,
    neutral: 0,
    negative: 0
  };
  const total =
    sentimentBreakdown.positive +
    sentimentBreakdown.neutral +
    sentimentBreakdown.negative;
  if (total === 0) return 0;
  return (
    ((sentimentBreakdown.positive + sentimentBreakdown.neutral * 0.5) / total) *
    100
  );
};

const buildRankMap = (sortedBusinesses) => {
  const map = new Map();
  sortedBusinesses.forEach((business, index) => {
    const key = getBusinessKey(business);
    if (key && !map.has(key)) {
      map.set(key, index + 1);
    }
  });
  return map;
};

/**
 * Localytics Ranking Service - Business Performance Score
 *
 * The Localytics Score is calculated on a 0-100 scale where 100 is the best.
 * 
 * Each of the 5 categories contributes up to 20 points:
 * - Growth: Points based on 3-month review growth rank
 * - Total Reviews: Points based on total review count rank
 * - Traffic: Points based on website traffic rank
 * - Sentiment: Points based on positive sentiment percentage rank
 * - SEO: Points based on SEO score rank (Monthly Visitors / Search Rank)
 *
 * Scoring Formula for Each Category:
 * Points = max(0, 21 - rank)
 * 1st place = 20 points, 2nd = 19, ..., 20th = 1 point, 21st+ = 0 points
 * This is the same regardless of how many competitors there are.
 * 
 * Total Score = Growth Points + Reviews Points + Traffic Points + Sentiment Points + SEO Points
 *
 * Examples:
 * - Ranked #1 in all 5 categories: 20 + 20 + 20 + 20 + 20 = 100 (Perfect!)
 * - Ranked #1 in 3, #5 in 2: 20 + 20 + 20 + 16 + 16 = 92 (Exceptional)
 * - Ranked #5 in all: 16 + 16 + 16 + 16 + 16 = 80 (Exceptional)
 *
 * Score Interpretation (position-based, groups of 3):
 * - Rank 1-3: Excellent (top-tier market leader)
 * - Rank 4-6: Strong (strong market performance)
 * - Rank 7-9: Great (consistently solid results)
 * - Rank 10-12: Average (middle of the pack)
 * - Rank 13-15: Below Average (room for growth)
 * - Rank 16+: Needs Improvement (focus on fundamentals)
 *
 * Higher score = better performance!
 */

/**
 * Calculate Localytics Ranking from 5 category rankings
 * @param {number} growthRank - Rank based on review growth
 * @param {number} totalReviewsRank - Rank based on total reviews
 * @param {number} trafficRank - Rank based on web traffic
 * @param {number} sentimentRank - Rank based on sentiment
 * @param {number} seoRank - Rank based on SEO score
 * @returns {number|null} Average ranking or null if insufficient data
 */
export const calculateLocalyticsRanking = (growthRank, totalReviewsRank, trafficRank, sentimentRank, seoRank) => {
  const ranks = [growthRank, totalReviewsRank, trafficRank, sentimentRank, seoRank];
  const validRanks = ranks.filter(r => r !== 'N/A' && r !== null && r !== undefined && !isNaN(r) && r > 0);
  
  if (validRanks.length === 0) return null;
  
  const sum = validRanks.reduce((acc, rank) => acc + rank, 0);
  const average = sum / validRanks.length;
  
  return Math.max(1.0, average);
};

/**
 * Calculate Localytics Score (out of 100) from category rankings
 * Each category awards points based on ranking position where 1st place = 20 points.
 * 
 * Formula per category: Points = max(0, 21 - rank)
 * 1st = 20 points, 2nd = 19, ..., 20th = 1 point, 21st+ = 0 points
 * This is the same regardless of how many competitors there are.
 * 
 * Total Score = sum of all 5 category scores (max 100)
 * 
 * @param {Object} rankings - Object with growth, total_reviews, traffic, sentiment, seo ranks
 * @param {Object} totalBusinesses - Object with total count for each category (kept for API compatibility)
 * @returns {number} Score out of 100 (0-100, where 100 is best)
 */
export const calculateLocalyticsScore = (rankings, totalBusinesses) => {
  const categories = ['growth', 'total_reviews', 'traffic', 'sentiment', 'seo'];
  let totalScore = 0;
  let validCategories = 0;

  categories.forEach(category => {
    const rank = rankings[category];
    
    if (rank && rank !== 'N/A' && !isNaN(rank) && rank > 0) {
      // Calculate points: 20 points max per category
      // Formula: max(0, 21 - rank)
      // 1st = 20 points, 2nd = 19, ..., 20th = 1 point, 21st+ = 0 points
      const categoryScore = Math.max(0, 21 - rank);
      totalScore += categoryScore;
      validCategories++;
    }
  });

  if (validCategories === 0) return 0;

  // Return total score out of 100
  return Math.round(totalScore);
};

/**
 * Get the tier information based on Localytics Score (0-100)
 * @param {number} score - The calculated score (0-100, where 100 is best)
 * @param {number} totalCompetitors - Total number of businesses in market (unused, kept for compatibility)
 * @returns {Object} Tier info with label, emoji, color, and colorScheme
 */
export const getRankingTier = (score, totalCompetitors, scoreRank) => {
  if (!score && score !== 0) return { label: 'N/A', emoji: '📊', color: 'gray.500', colorScheme: 'gray' };

  if (scoreRank && scoreRank > 0) {
    if (scoreRank <= 3) return { label: 'Excellent', emoji: '⚡', color: 'blue.500', colorScheme: 'blue' };
    if (scoreRank <= 6) return { label: 'Good', emoji: '✅', color: 'green.500', colorScheme: 'green' };
    if (scoreRank <= 9) return { label: 'Fair', emoji: '📊', color: 'teal.500', colorScheme: 'teal' };
    if (scoreRank <= 12) return { label: 'Below Average', emoji: '📉', color: 'yellow.500', colorScheme: 'yellow' };
    if (scoreRank <= 15) return { label: 'Poor', emoji: '⚠️', color: 'orange.500', colorScheme: 'orange' };
    return { label: 'Needs Improvement', emoji: '🔧', color: 'red.500', colorScheme: 'red' };
  }

  if (score >= 85) return { label: 'Excellent', emoji: '⚡', color: 'blue.500', colorScheme: 'blue' };
  if (score >= 70) return { label: 'Good', emoji: '✅', color: 'green.500', colorScheme: 'green' };
  if (score >= 55) return { label: 'Fair', emoji: '📊', color: 'teal.500', colorScheme: 'teal' };
  if (score >= 40) return { label: 'Below Average', emoji: '📉', color: 'yellow.500', colorScheme: 'yellow' };
  if (score >= 25) return { label: 'Poor', emoji: '⚠️', color: 'orange.500', colorScheme: 'orange' };
  return { label: 'Needs Improvement', emoji: '🔧', color: 'red.500', colorScheme: 'red' };
};

/**
 * Get color for the score display
 * Higher scores get green, middle scores get yellow, lower scores get red
 * @param {number} score - The calculated score (0-100, where 100 is best)
 * @param {number} totalCompetitors - Total number of businesses (unused, kept for compatibility)
 * @returns {string} Hex color
 */
export const getRankingColor = (score, totalCompetitors = 20) => {
  if (!score && score !== 0) return '#94a3b8'; // Gray for N/A
  
  // Normalize score to 0-1 range
  const normalizedScore = Math.max(0, Math.min(1, score / 100));
  
  // Traffic light color scheme:
  // High scores (80-100) → Green
  // Mid scores (50-79) → Yellow/Orange
  // Low scores (0-49) → Red
  
  let r, g, b;
  
  if (normalizedScore >= 0.8) {
    // Green range (80-100): Pure green to slightly yellow-green
    const greenPosition = (normalizedScore - 0.8) / 0.2; // 0 to 1
    r = Math.round(34 + (0 - 34) * greenPosition);      // 34 → 0
    g = Math.round(197 + (200 - 197) * greenPosition);   // 197 → 200
    b = Math.round(94 + (0 - 94) * greenPosition);       // 94 → 0
  } else if (normalizedScore >= 0.5) {
    // Yellow range (50-79): Orange-yellow to yellow-green
    const yellowPosition = (normalizedScore - 0.5) / 0.3; // 0 to 1
    r = Math.round(255 - (255 - 34) * yellowPosition);    // 255 → 34 (orange to green)
    g = Math.round(165 + (197 - 165) * yellowPosition);   // 165 → 197
    b = Math.round(0 + (94 - 0) * yellowPosition);        // 0 → 94
  } else {
    // Red range (0-49): Dark red to orange-red
    const redPosition = normalizedScore / 0.5; // 0 to 1
    r = Math.round(139 + (255 - 139) * redPosition);      // 139 → 255 (dark red to bright)
    g = Math.round(0 + (165 - 0) * redPosition);          // 0 → 165
    b = Math.round(0);                                     // 0
  }
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

/**
 * Format score for display
 * @param {number} score - The score value (0-100)
 * @param {boolean} showDecimal - Whether to show decimal places (default: false for whole numbers)
 * @returns {string} Formatted score string
 */
export const formatRanking = (score, showDecimal = false) => {
  if (score === null || score === undefined) return 'N/A';
  return showDecimal ? score.toFixed(1) : Math.round(score).toString();
};

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 * @param {number} n - The number
 * @returns {string} Number with ordinal suffix
 */
export const getOrdinal = (n) => {
  if (n === 'N/A' || n === null || n === undefined) return 'N/A';
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * Calculate rankings for all businesses from review data
 * @param {Array} reviewsData - Array of business review data
 * @param {Array} trafficData - Array of business traffic data (optional)
 * @returns {Object} Object containing sorted arrays and ranking data
 */
export const calculateAllRankings = (reviewsData, yearData = []) => {
  if (!reviewsData || reviewsData.length === 0) {
    return {
      businesses: [],
      sortedByGrowth: [],
      sortedByTotal: [],
      sortedBySentiment: [],
      sortedByTraffic: [],
      rankingResults: [],
      totalBusinesses: 0
    };
  }

  const businessesWithHistory = reviewsData.map((business) => {
    const historicalDataRecord = yearData.find(
      (yearItem) =>
        yearItem?.maps_uri === business.maps_uri ||
        yearItem?.mapsUri === business.maps_uri ||
        yearItem?.name === business.name
    );

    return {
      ...business,
      historicalData: normalizeHistoricalData(historicalDataRecord)
    };
  });

  const businessesWithMetrics = businessesWithHistory.map((business) => {
    const historicalData = business.historicalData || DEFAULT_HISTORICAL_DATA;
    const realSeoScore = calculateSeoScore(business);
    const seoScoreEstimated = realSeoScore > 0
      ? realSeoScore
      : estimateSeoScore(business);
    return {
      ...business,
      historicalData,
      threeMonthGrowth: calculateThreeMonthGrowth(historicalData),
      sentimentScore: calculateSentimentScore(historicalData),
      trafficValue: getTrafficValue(business) || 0,
      seoScore: realSeoScore,
      seoScoreEstimated,
      seoScoreIsEstimate: !(realSeoScore > 0)
    };
  });

  // Sort by 3-month growth (higher is better)
  const sortedByGrowth = [...businessesWithMetrics].sort(
    (a, b) => b.threeMonthGrowth - a.threeMonthGrowth
  );

  // Sort by total reviews (higher is better)
  const sortedByTotal = [...businessesWithMetrics].sort(
    (a, b) => (b.rating_count || 0) - (a.rating_count || 0)
  );

  // Sort by sentiment (higher positive % is better)
  const sortedBySentiment = [...businessesWithMetrics].sort(
    (a, b) => b.sentimentScore - a.sentimentScore
  );

  // Sort by traffic (higher is better)
  const sortedByTraffic = [...businessesWithMetrics]
    .filter((business) => business.trafficValue > 0)
    .sort((a, b) => b.trafficValue - a.trafficValue);

  // Sort by SEO score (higher is better). We sort by `seoScoreEstimated`
  // so every business always gets an SEO rank — businesses that lack
  // SpyFu-enriched traffic data fall back to the estimate (derived from
  // search rank, review volume, rating, and any traffic signal) instead
  // of being dropped, which previously surfaced as a literal "N/A" rank.
  const sortedBySeo = sortBusinessesBySeoScore(businessesWithMetrics);

  const growthRankMap = buildRankMap(sortedByGrowth);
  const totalReviewsRankMap = buildRankMap(sortedByTotal);
  const sentimentRankMap = buildRankMap(sortedBySentiment);
  const trafficRankMap = buildRankMap(sortedByTraffic);
  const seoRankMap = buildRankMap(sortedBySeo);

  const totalBusinesses = businessesWithMetrics.length;

  // Calculate ranking for each business
  const rankingResults = businessesWithMetrics.map((business) => {
    const businessKey = getBusinessKey(business);
    const growthRank = growthRankMap.get(businessKey) || null;
    const totalReviewsRank = totalReviewsRankMap.get(businessKey) || null;
    const sentimentRank = sentimentRankMap.get(businessKey) || null;
    const trafficRank = trafficRankMap.get(businessKey) || null;
    const seoRank = seoRankMap.get(businessKey) || null;

    const localyticsRanking = calculateLocalyticsRanking(
      growthRank,
      totalReviewsRank,
      trafficRank || totalBusinesses,
      sentimentRank,
      seoRank || totalBusinesses
    );

    const rankings = {
      growth: growthRank || 'N/A',
      total_reviews: totalReviewsRank || 'N/A',
      sentiment: sentimentRank || 'N/A',
      traffic: trafficRank || 'N/A',
      seo: seoRank || 'N/A'
    };

    const totalCounts = {
      growth: sortedByGrowth.length,
      total_reviews: sortedByTotal.length,
      sentiment: sortedBySentiment.length,
      traffic: sortedByTraffic.length,
      seo: sortedBySeo.length
    };

    const localyticsScore = calculateLocalyticsScore(rankings, totalCounts);

    return {
      business_name: business.name || business.business_name,
      maps_uri: business.maps_uri || business.mapsUri || '',
      localytics_ranking: localyticsRanking,
      localytics_score: localyticsScore,
      rankings,
      raw_data: {
        growth: business.threeMonthGrowth || 0,
        total_reviews: business.rating_count || 0,
        sentiment: business.sentimentScore || 0,
        traffic: business.trafficValue || 0,
        seo: business.seoScore || 0
      }
    };
  });

  // Sort by localytics score (higher is better)
  rankingResults.sort((a, b) => {
    if (a.localytics_score === null || a.localytics_score === undefined) return 1;
    if (b.localytics_score === null || b.localytics_score === undefined) return -1;
    return b.localytics_score - a.localytics_score; // Descending order (highest first)
  });

  return {
    businesses: businessesWithMetrics,
    sortedByGrowth,
    sortedByTotal,
    sortedBySentiment,
    sortedByTraffic,
    sortedBySeo,
    rankingResults,
    totalBusinesses,
    totalWithTraffic: sortedByTraffic.length,
    totalWithSeo: sortedBySeo.length
  };
};

/**
 * Fetch and calculate Localytics Rankings for all businesses
 * @returns {Promise<Object>} Rankings data with sorted arrays and results
 */
export async function getLocalyticsRankings() {
  try {
    logger.log('Fetching data for Localytics Rankings...');
    
    // Fetch reviews data
    const reviewsData = await getReviews();

    let yearData = [];
    try {
      yearData = await getYearData();
    } catch (err) {
      logger.log('Year data not available:', err.message);
    }

    // Calculate all rankings
    const rankings = calculateAllRankings(reviewsData, yearData);
    
    logger.log('Localytics Scores calculated:', {
      totalBusinesses: rankings.totalBusinesses,
      topScore: rankings.rankingResults[0]?.localytics_score,
      bottomScore: rankings.rankingResults[rankings.rankingResults.length - 1]?.localytics_score
    });

    return {
      ...rankings,
      yearData
    };
  } catch (error) {
    logger.error('Error calculating Localytics Rankings:', error);
    throw error;
  }
}

/**
 * Calculate insights from ranking results
 * @param {Array} rankingResults - Array of ranking results
 * @returns {Object} Insights about the scores
 */
export function calculateRankingInsights(rankingResults) {
  if (!rankingResults || rankingResults.length === 0) {
    return null;
  }

  const validScores = rankingResults.filter(r => r.localytics_score !== null && r.localytics_score !== undefined);
  const totalBusinesses = rankingResults.length;
  
  const avgScore = validScores.reduce((sum, r) => sum + r.localytics_score, 0) / validScores.length;
  
  // Count businesses in each tier (position-based, groups of 3)
  const excellent = validScores.slice(0, 3).length;
  const strong = validScores.slice(3, 6).length;
  const great = validScores.slice(6, 9).length;
  const average = validScores.slice(9, 12).length;
  const belowAverage = validScores.slice(12, 15).length;
  const needsImprovement = validScores.slice(15).length;
  
  return {
    totalBusinesses,
    averageScore: Math.round(avgScore),
    excellent,
    strong,
    great,
    average,
    belowAverage,
    needsImprovement,
    topPerformer: validScores[0] || null,
    scoreRange: {
      best: validScores[0]?.localytics_score || null,
      worst: validScores[validScores.length - 1]?.localytics_score || null
    }
  };
}

// Export service object for convenience
const localyticsRankingService = {
  getLocalyticsRankings,
  calculateLocalyticsRanking,
  calculateAllRankings,
  calculateRankingInsights,
  getRankingTier,
  getRankingColor,
  formatRanking,
  getOrdinal
};

export default localyticsRankingService;
